import type React from 'react';
import { useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { styled } from '@mui/material/styles';
import { CSSTransition } from 'react-transition-group';
import tinycolor from 'tinycolor2';

// Redux
import {
  setCsatFeedback,
  setCsatResolve,
  setCsatVisibility,
  setIsCsatDataSubmitted,
  setIsCsatEndTransition,
  setIsCsatSurveyFinished,
} from 'actions/workflows/workflows';
import {
  getTranslatedCsatText,
  submitCsatV2,
} from 'actions/workflows-v2/workflowsV2';
import {
  selectIsInteractiveEmail,
  selectLegibleTextColor,
  selectThemeColor,
} from 'reducers/config';
import {
  selectCsatFormData,
  selectCsatConfig,
  selectWorkflowsConfig,
  selectTranslatedCsatText,
  selectCsatSurveyVisibilityOnWorkflow,
  selectIsCsatTextEmptyFromResponse,
  selectIsBuilderPreview,
  selectIsMobileSDK,
} from 'reducers/workflows';
import { selectIsMobile } from 'reducers/main';
import { closeWidget } from 'actions/main';

// Helpers
import { getFeedbackOptions } from './utils';

import CustomerSatisfactionCard from './CustomerSatisfactionCard';
import { useTheme } from '@mui/material/styles';
import { useEffect } from 'react';
import OptionButton from 'components/OptionButton';
import { sendResizeiframeMessage } from 'components/App/utils';
import { selectLegibleButtonOutlineColor } from 'reducers/config';

const CustomerSatisfactionForm: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { csatFeedback, csatResolve, csatScore } =
    useAppSelector(selectCsatFormData);
  const {
    isCsatDetailVisible,
    isCsatEndTransition,
    isCsatVisible = false,
  } = useAppSelector(selectCsatConfig);
  const workflowsOrgConfig = useAppSelector(selectWorkflowsConfig);
  const translatedCsatText = useAppSelector(selectTranslatedCsatText);
  const shouldFetchTranslatedCsatText = useAppSelector(
    selectCsatSurveyVisibilityOnWorkflow,
  );
  const isCsatTextEmptyFromResponse = useAppSelector(
    selectIsCsatTextEmptyFromResponse,
  );
  const dialogRef = useRef<HTMLDialogElement>(null);
  const isBuilderPreview = useAppSelector(selectIsBuilderPreview);
  const isInteractiveEmail = useAppSelector(selectIsInteractiveEmail);

  const isMobile = useAppSelector(selectIsMobile);
  const isMobileSDK = useAppSelector(selectIsMobileSDK);
  const themeColor = useAppSelector(selectThemeColor);
  const legibleTextColor = useAppSelector(selectLegibleTextColor);
  const legibleButtonOutlineColor = useAppSelector(
    selectLegibleButtonOutlineColor,
  );
  const isGrayColor = themeColor === theme.palette.colors.slate[200];
  let spacerColor;
  if (isGrayColor)
    spacerColor = tinycolor(theme.palette.colors.slate[200])
      .lighten(25)
      .toString();
  else
    spacerColor = tinycolor(theme.palette.colors.slate[200])
      .darken(6)
      .toString();

  const isBlackColor = themeColor === theme.palette.colors.black;
  const isCustomTheme = theme.palette.primary.main !== themeColor;
  const isSubmitBtnEnabled =
    csatScore > 0 && csatFeedback.length > 0 && csatResolve !== undefined;

  const submitBtnBackgroundColor =
    !isCustomTheme && !isSubmitBtnEnabled
      ? theme.palette.colors.grey[500]
      : themeColor;

  const setButtonHoveredColor = (color: string) => {
    return isBlackColor
      ? tinycolor(color).lighten(25).toString()
      : tinycolor(color).darken(6).toString();
  };

  const submitData = ({
    end = false,
    submit,
    timeout = 0,
  }: {
    end?: boolean;
    submit: boolean;
    timeout?: number;
  }) => {
    if (submit && workflowsOrgConfig?.csat_survey_config?.is_enabled) {
      dispatch(submitCsatV2());
    }

    /** When submit is clicked it will activate a transition animation based off of selectsCsatEndTransition (1s)
     * Then will reset form fields and close solve chat (based off of timeout param - 1.5s)
     */
    if (end) {
      setTimeout(() => {
        dispatch(setIsCsatSurveyFinished(true));
        dispatch(setIsCsatEndTransition(false));
        dispatch(setCsatVisibility(false));

        if (isInteractiveEmail || isBuilderPreview) {
          return;
        }

        if (isMobileSDK) {
          // Don't close on mobile. If we close it, the next time it opens it
          // fires the `forethoughtWidgetClosed` event and immediately closes
          // the widget (making it impossible to open the widget), but do send
          // the resize event so iOS closes the modal
          sendResizeiframeMessage(false);
        } else {
          dispatch(closeWidget());
          sendResizeiframeMessage(false);
        }
      }, timeout);
    }
  };

  const feedbackOptions = getFeedbackOptions(csatScore);
  useEffect(() => {
    if (
      workflowsOrgConfig &&
      (isCsatVisible || shouldFetchTranslatedCsatText)
    ) {
      dispatch(getTranslatedCsatText());
    }
  }, [
    isCsatVisible,
    shouldFetchTranslatedCsatText,
    dispatch,
    workflowsOrgConfig,
  ]);

  const isHTMLDialogElementSupported =
    // eslint-disable-next-line compat/compat -- feature detection
    typeof window.HTMLDialogElement === 'function';

  useEffect(() => {
    if (!isHTMLDialogElementSupported) {
      return;
    }

    if (isCsatVisible) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [isCsatVisible, isHTMLDialogElementSupported]);

  if (isCsatTextEmptyFromResponse) return;

  return (
    <>
      <GrayBackground isMobile={isMobile} isVisible={isCsatVisible} />
      <Container
        aria-label='Customer satisfaction survey'
        as={isHTMLDialogElementSupported ? 'dialog' : 'div'}
        isExpanded={isCsatDetailVisible}
        isMobile={isMobile}
        isVisible={isCsatVisible}
        ref={dialogRef}
      >
        <ContentContainer
          backgroundColor={theme.palette.colors.white}
          isExpanded={isCsatDetailVisible}
          isMobile={isMobile}
        >
          <CustomerSatisfactionCard />
          {isCsatDetailVisible ? (
            <>
              <ExpandedDetailSpacer spacerColor={spacerColor} />
              <Message
                color={theme.palette.text.primary}
                data-testid='csat-detail-option-message'
              >
                {translatedCsatText?.text.CSAT_TELL_US_MORE}
              </Message>
              <ExpandedFeedbackContainer
                title={translatedCsatText?.text.CSAT_TELL_US_MORE}
              >
                {feedbackOptions.map((option, index) => {
                  const foundIndex = csatFeedback.findIndex(
                    value => value === translatedCsatText?.text[option.value],
                  );
                  const translatedOptionText =
                    translatedCsatText?.text[option.value] || '';

                  return (
                    <OptionButton
                      isMobile={isMobile}
                      isSelected={foundIndex >= 0}
                      key={translatedOptionText || index}
                      onClick={() => {
                        const updatedCsatFeedback = [...csatFeedback];
                        foundIndex !== -1
                          ? updatedCsatFeedback.splice(foundIndex, 1)
                          : updatedCsatFeedback.push(translatedOptionText);
                        dispatch(setCsatFeedback(updatedCsatFeedback));
                        submitData({ submit: true });
                      }}
                      text={translatedOptionText}
                    />
                  );
                })}
              </ExpandedFeedbackContainer>
              <ExpandedDetailSpacer spacerColor={spacerColor} />
              <Message
                color={theme.palette.text.primary}
                data-testid='csat-detail-resolve'
              >
                {translatedCsatText?.text.CSAT_RESOLVE_QUESTION}
              </Message>
              <ResolveOptionContainer
                title={translatedCsatText?.text.CSAT_RESOLVE_QUESTION}
              >
                <OptionButton
                  isSelected={csatResolve}
                  onClick={() => {
                    dispatch(setCsatResolve(true));
                    submitData({ submit: true });
                  }}
                  padding={isMobile ? '2% 18%' : '5px 51px'}
                  text={translatedCsatText?.text.FT_YES || ''}
                />
                <OptionButton
                  isSelected={csatResolve === false}
                  key={translatedCsatText?.text.FT_NO}
                  onClick={() => {
                    dispatch(setCsatResolve(false));
                    submitData({ submit: true });
                  }}
                  padding={isMobile ? '2% 18%' : '5px 51px'}
                  text={translatedCsatText?.text.FT_NO || ''}
                />
              </ResolveOptionContainer>
              <ExpandedDetailFooter isCsatEndTransition={isCsatEndTransition}>
                {!isCsatEndTransition && (
                  <>
                    <SubmitButton
                      backgroundColor={themeColor}
                      color={legibleTextColor}
                      data-testid='csat-submit-button'
                      disabled={!isSubmitBtnEnabled}
                      hoveredColor={setButtonHoveredColor(
                        submitBtnBackgroundColor,
                      )}
                      onClick={() => {
                        dispatch(setIsCsatDataSubmitted(true));
                        dispatch(setIsCsatEndTransition(true));
                        submitData({
                          end: true,
                          submit: true,
                          timeout: 1500,
                        });
                      }}
                      outlineColor={legibleButtonOutlineColor}
                      type='submit'
                    >
                      {translatedCsatText?.text.CSAT_SUBMIT}
                    </SubmitButton>
                    <DismissButton
                      color={theme.palette.colors.grey[800]}
                      data-testid='csat-dismiss-button'
                      isCsatDetailVisible={isCsatDetailVisible}
                      onClick={() => {
                        dispatch(setIsCsatEndTransition(true));
                        submitData({
                          end: true,
                          submit: false,
                          timeout: 1500,
                        });
                      }}
                    >
                      {translatedCsatText?.text.CSAT_CLOSE}
                    </DismissButton>
                  </>
                )}
                <CSSTransition
                  classNames='fade'
                  in={isCsatEndTransition}
                  mountOnEnter
                  timeout={1000}
                  unmountOnExit
                >
                  <Message
                    color={theme.palette.text.primary}
                    data-testid='csat-thank-you-message'
                    fontSize={18}
                  >
                    {translatedCsatText?.text.CSAT_THANK_YOU}
                  </Message>
                </CSSTransition>
              </ExpandedDetailFooter>
            </>
          ) : (
            <DismissButton
              color={theme.palette.colors.grey[800]}
              data-testid='csat-dismiss-button'
              onClick={() =>
                submitData({
                  end: true,
                  submit: false,
                })
              }
            >
              {translatedCsatText?.text.CSAT_SKIP_AND_CLOSE}
            </DismissButton>
          )}
        </ContentContainer>
      </Container>
    </>
  );
};

export default CustomerSatisfactionForm;

const Container = styled('dialog')<{
  isExpanded: boolean;
  isMobile?: boolean;
  isVisible: boolean;
}>`
  position: fixed;
  bottom: 0px;
  left: 0px;
  right: 0px;
  z-index: 6;
  display: ${props => (props.isVisible ? 'flex' : 'none')};
  flex-direction: column;
  justify-content: flex-end;
  height: ${props =>
    props.isExpanded
      ? props.isMobile
        ? '100%'
        : 'calc(100% - 24px * 2)'
      : undefined};
  width: ${props => (props.isMobile ? '100%' : 'calc(100% - 24px * 2)')};
  padding: 0;
  top: 0;
  transition: all 0.3s linear;
  border: none;
  margin: ${props =>
    props.isExpanded
      ? props.isMobile
        ? '0'
        : '24px'
      : props.isMobile
        ? 'auto 0 0'
        : 'auto 24px 24px'};
  max-width: 100%;
  max-height: 100%;
  border-radius: ${props => !props.isMobile && '9px'};

  ::backdrop {
    background-color: transparent;
  }
`;

const ContentContainer = styled('div')<{
  backgroundColor: string;
  isExpanded: boolean;
  isMobile?: boolean;
}>`
  background-color: ${props => props.backgroundColor};
  padding: ${props => (props.isExpanded ? '0 32px' : '40px 32px 32px 32px')};
  border-radius: ${props => (props.isMobile ? '0' : '9')}px;
  display: flex;
  flex-direction: column;
  margin-bottom: 0px;
  height: ${props => (props.isExpanded ? '100%' : undefined)};
  ::-webkit-scrollbar {
    display: none;
  }
  transition: all 0.3s linear;
`;

const Message = styled('p')<{ color: string; fontSize?: number }>`
  color: ${props => props.color};
  padding: 0 10px;
  margin: 0;
  font-size: ${props => (props.fontSize ? props.fontSize : 16)}px;
  font-weight: 400;
  line-height: 26px;
  text-align: center;
  &.fade-enter {
    opacity: 0;
  }
  &.fade-enter-active {
    opacity: 1;
    transition: opacity 1s;
  }
`;

const SubmitButton = styled('button')<{
  backgroundColor: string;
  color: string;
  hoveredColor: string;
  outlineColor: string;
}>`
  width: 100%;
  color: ${props => props.color};
  cursor: pointer;
  border-radius: 5px;
  padding: 9px 0;
  border: none;
  background-color: ${props => props.backgroundColor};
  opacity: ${props => (props.disabled ? ' 30%' : 'none')};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  margin-bottom: 5px;
  font-weight: 500;

  &:hover {
    background-color: ${props =>
      props.disabled ? props.backgroundColor : props.hoveredColor};
  }

  :focus {
    outline: 2px solid ${props => props.outlineColor};
  }
`;

const DismissButton = styled('button')<{
  color: string;
  isCsatDetailVisible?: boolean;
}>`
  border: none;
  cursor: pointer;
  background-color: transparent;
  color: ${props => props.color};
  padding: ${props =>
    props.isCsatDetailVisible ? '0px 20px 16px 20px' : '24px 20px 0px 20px'};
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  height: 40px;
  margin-bottom: 2px;

  :focus {
    outline: 2px solid var(--legible-element-color);
  }
`;

const GrayBackground = styled('div')<{
  isMobile?: boolean;
  isVisible?: boolean;
}>`
  background-color: #000000;
  opacity: 30%;
  position: ${props => (props.isVisible ? 'absolute' : 'static')};
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  z-index: 6;
  border-radius: 9px 9px 15px 15px;
  padding: ${props => (props.isMobile && props.isVisible ? '24' : '0')}px;
  margin: ${props => !props.isMobile && props.isVisible && '24px'};
`;

const ExpandedDetailFooter = styled('div')<{
  isCsatEndTransition?: boolean;
}>`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  padding: ${props => (props.isCsatEndTransition ? '0px 0px 30px 0px' : '0px')};
`;

const ExpandedDetailSpacer = styled('div')<{ spacerColor?: string }>`
  border-width: 1px;
  border-style: solid;
  border-bottom: 0px;
  border-color: ${props => props.spacerColor};
  margin: 24px 0px 32px 0px;
`;

const ExpandedFeedbackContainer = styled('form')`
  display: block;
  cursor: pointer;
  padding: 16px 0px 0px 0px;
  text-align: center;
`;

const ResolveOptionContainer = styled('form')`
  display: flex;
  padding: 16px 0px 24px 0px;
  justify-content: center;
`;
